import React, { useMemo, useState, useEffect } from 'react';
import { ProductOption, ProductOptionChoice, Stock } from '../types';
import ProductStockSelector from './ProductStockSelector';
import { AllergenManager } from './AllergenManager';
import { apiService } from '../services/api';
import './ProductOptionsManager.css';

interface ProductOptionsManagerProps {
    options: ProductOption[];
    onChange: (options: ProductOption[]) => void;
    stocks: Stock[];
    stocksLoading?: boolean;
    onRefreshStocks?: () => Promise<void> | void;
    disabled?: boolean;
    hideCommonOptions?: boolean; // Hide common options button and features
}

export const ProductOptionsManager: React.FC<ProductOptionsManagerProps> = ({
    options,
    onChange,
    stocks,
    stocksLoading = false,
    onRefreshStocks,
    disabled = false,
    hideCommonOptions = false
}) => {
    const [errors, setErrors] = useState<Record<string, string[]>>({});
    const [commonOptions, setCommonOptions] = useState<ProductOption[]>([]);
    const [commonOptionsLoading, setCommonOptionsLoading] = useState(false);
    const [showCommonOptionsDropdown, setShowCommonOptionsDropdown] = useState(false);

    const stockListMemo = useMemo(() => stocks, [stocks]);

    // Load common options
    useEffect(() => {
        loadCommonOptions();
    }, []);

    const loadCommonOptions = async () => {
        try {
            setCommonOptionsLoading(true);
            const options = await apiService.getCommonOptions();
            setCommonOptions(options);
        } catch (error) {
            console.error('Failed to load common options:', error);
        } finally {
            setCommonOptionsLoading(false);
        }
    };

    const addOption = () => {
        const maxOrder = options.reduce((max, opt) => Math.max(max, opt.order ?? 0), 0);
        const newOption: ProductOption = {
            id: `option_${Date.now()}`,
            name: 'New Option',
            type: 'single',
            required: false,
            choices: [
                {
                    id: `choice_${Date.now()}`,
                    name: 'Option 1',
                    extraCost: 0,
                    defaultSelected: false,
                    stockIds: [],
                    order: 0
                }
            ],
            order: maxOrder + 1
        };
        onChange([...options, newOption]);
    };

    const moveOption = (optionId: string, direction: 'up' | 'down') => {
        // First, normalize all options to have sequential order values
        const sortedOptions = [...options].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
        const normalizedOptions = sortedOptions.map((opt, index) => ({
            ...opt,
            order: index
        }));

        const currentIndex = normalizedOptions.findIndex(opt => opt.id === optionId);

        if (currentIndex === -1) return;
        if (direction === 'up' && currentIndex === 0) return;
        if (direction === 'down' && currentIndex === normalizedOptions.length - 1) return;

        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

        // Swap the order values
        const temp = normalizedOptions[currentIndex].order;
        normalizedOptions[currentIndex].order = normalizedOptions[swapIndex].order;
        normalizedOptions[swapIndex].order = temp;

        onChange(normalizedOptions);
    };

    const addCommonOption = (commonOption: ProductOption) => {
        const maxOrder = options.reduce((max, opt) => Math.max(max, opt.order ?? 0), 0);
        // Create a copy of the common option with new IDs
        const newOption: ProductOption = {
            ...commonOption,
            id: `option_${Date.now()}`,
            isCommonOption: true,
            commonOptionSourceId: commonOption.id,
            order: maxOrder + 1,
            choices: commonOption.choices.map((choice, index) => ({
                ...choice,
                id: `choice_${Date.now()}_${index}`,
                order: choice.order ?? index
            }))
        };
        onChange([...options, newOption]);
        setShowCommonOptionsDropdown(false);
    };

    const updateOption = (optionId: string, field: keyof ProductOption, value: any) => {
        const updatedOptions = options.map(option => {
            if (option.id === optionId) {
                const updatedOption = { ...option, [field]: value };

                // Special handling for type changes
                if (field === 'type') {
                    if (value === 'checkbox') {
                        // Checkbox can only have one choice and cannot be required
                        updatedOption.choices = updatedOption.choices.slice(0, 1);
                        updatedOption.required = false;
                    }
                }

                return updatedOption;
            }
            return option;
        });
        onChange(updatedOptions);
        validateOptions(updatedOptions);
    };

    const deleteOption = (optionId: string) => {
        const updatedOptions = options.filter(opt => opt.id !== optionId);
        onChange(updatedOptions);
        validateOptions(updatedOptions);
    };

    const addChoice = (optionId: string) => {
        const updatedOptions = options.map(option => {
            if (option.id === optionId) {
                const maxOrder = option.choices.reduce((max, ch) => Math.max(max, ch.order ?? 0), 0);
                const newChoice: ProductOptionChoice = {
                    id: `choice_${Date.now()}`,
                    name: `Choice ${option.choices.length + 1}`,
                    extraCost: 0,
                    defaultSelected: false,
                    stockIds: [],
                    order: maxOrder + 1
                };
                return {
                    ...option,
                    choices: [...option.choices, newChoice]
                };
            }
            return option;
        });
        onChange(updatedOptions);
    };

    const moveChoice = (optionId: string, choiceId: string, direction: 'up' | 'down') => {
        const updatedOptions = options.map(option => {
            if (option.id !== optionId) return option;

            // First, normalize all choices to have sequential order values
            const sortedChoices = [...option.choices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
            const normalizedChoices = sortedChoices.map((ch, index) => ({
                ...ch,
                order: index
            }));

            const currentIndex = normalizedChoices.findIndex(ch => ch.id === choiceId);

            if (currentIndex === -1) return option;
            if (direction === 'up' && currentIndex === 0) return option;
            if (direction === 'down' && currentIndex === normalizedChoices.length - 1) return option;

            const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;

            // Swap the order values
            const temp = normalizedChoices[currentIndex].order;
            normalizedChoices[currentIndex].order = normalizedChoices[swapIndex].order;
            normalizedChoices[swapIndex].order = temp;

            return { ...option, choices: normalizedChoices };
        });

        onChange(updatedOptions);
    };

    const toggleChoiceStock = (optionId: string, choiceId: string, stockId: string) => {
        const updatedOptions = options.map(option => {
            if (option.id !== optionId) {
                return option;
            }

            const updatedChoices = option.choices.map(choice => {
                if (choice.id !== choiceId) {
                    return choice;
                }

                const existingStockIds = Array.isArray(choice.stockIds) ? [...choice.stockIds] : [];
                const index = existingStockIds.indexOf(stockId);

                if (index >= 0) {
                    existingStockIds.splice(index, 1);
                } else {
                    existingStockIds.push(stockId);
                }

                return {
                    ...choice,
                    stockIds: existingStockIds
                };
            });

            return {
                ...option,
                choices: updatedChoices
            };
        });

        onChange(updatedOptions);
    };

    const updateChoice = (optionId: string, choiceId: string, field: keyof ProductOptionChoice, value: any) => {
        const updatedOptions = options.map(option => {
            if (option.id === optionId) {
                const updatedChoices = option.choices.map(choice => {
                    if (choice.id === choiceId) {
                        const updatedChoice = { ...choice, [field]: value };
                        // Remove undefined values to ensure clean JSON serialization
                        if (value === undefined) {
                            delete updatedChoice[field];
                        }
                        return updatedChoice;
                    }
                    return choice;
                });
                return { ...option, choices: updatedChoices };
            }
            return option;
        });
        onChange(updatedOptions);
        validateOptions(updatedOptions);
    };

    const deleteChoice = (optionId: string, choiceId: string) => {
        const updatedOptions = options.map(option => {
            if (option.id === optionId && option.choices.length > 1) { // Prevent deleting last choice
                return {
                    ...option,
                    choices: option.choices.filter(choice => choice.id !== choiceId)
                };
            }
            return option;
        });
        onChange(updatedOptions);
        validateOptions(updatedOptions);
    };

    const validateOptions = (optionsToValidate: ProductOption[]) => {
        const newErrors: Record<string, string[]> = {};

        optionsToValidate.forEach(option => {
            const optionErrors: string[] = [];

            if (!option.name.trim()) {
                optionErrors.push('Option name is required');
            }

            if (option.choices.length === 0) {
                optionErrors.push('At least one choice is required');
            }

            option.choices.forEach((choice, index) => {
                if (!choice.name.trim()) {
                    optionErrors.push(`Choice ${index + 1} name is required`);
                }
            });

            if (option.type === 'checkbox' && option.choices.length > 1) {
                optionErrors.push('Checkbox options can only have one choice');
            }

            if (optionErrors.length > 0) {
                newErrors[option.id] = optionErrors;
            }
        });

        setErrors(newErrors);
    };

    return (
        <div className="options-manager">
            <div className="options-header">
                <h3>Product Options</h3>
                <div className="header-actions">
                    <button
                        type="button"
                        className="add-option-btn"
                        onClick={addOption}
                    >
                        + Add Custom Option
                    </button>
                    {!hideCommonOptions && (
                        <div className="common-options-dropdown">
                            <button
                                type="button"
                                className="add-common-option-btn"
                                onClick={() => setShowCommonOptionsDropdown(!showCommonOptionsDropdown)}
                                disabled={commonOptionsLoading || commonOptions.length === 0}
                            >
                                🔗 Add Common Option
                            </button>
                            {showCommonOptionsDropdown && commonOptions.length > 0 && (
                                <div className="common-options-menu">
                                    {commonOptions.map(option => (
                                        <button
                                            key={option.id}
                                            type="button"
                                            className="common-option-item"
                                            onClick={() => addCommonOption(option)}
                                        >
                                            <span className="option-name">{option.name}</span>
                                            <span className="option-type">{option.type}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {options.length === 0 && (
                <div className="no-options">
                    <p>No options configured. Add options to allow customers to customize this product.</p>
                </div>
            )}

            <div className="options-list">
                {[...options].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((option, optionIndex, sortedOptions) => (
                    <div key={option.id} className="option-item">
                        {/* Option Header with inline editing */}
                        <div className="option-header-inline">
                            <div className="option-name-section">
                                <div className="reorder-buttons">
                                    <button
                                        type="button"
                                        className="reorder-btn"
                                        onClick={() => moveOption(option.id, 'up')}
                                        disabled={optionIndex === 0}
                                        title="Move option up"
                                    >
                                        ↑
                                    </button>
                                    <button
                                        type="button"
                                        className="reorder-btn"
                                        onClick={() => moveOption(option.id, 'down')}
                                        disabled={optionIndex === sortedOptions.length - 1}
                                        title="Move option down"
                                    >
                                        ↓
                                    </button>
                                </div>
                                <input
                                    type="text"
                                    value={option.name}
                                    onChange={(e) => updateOption(option.id, 'name', e.target.value)}
                                    className="option-name-input"
                                    placeholder="Option name (e.g., Size, Toppings)"
                                    disabled={option.isCommonOption && !hideCommonOptions}
                                />
                                {option.isCommonOption && !hideCommonOptions && (
                                    <span className="common-option-badge" title="This option is managed centrally. Edit it in Common Options page to update all products.">
                                        🔗 Managed Centrally
                                    </span>
                                )}
                                <button
                                    type="button"
                                    className="delete-option-btn"
                                    onClick={() => deleteOption(option.id)}
                                    title="Delete option"
                                >
                                    ✕
                                </button>
                            </div>

                            <div className="option-controls">
                                {!(option.isCommonOption && !hideCommonOptions) && (
                                    <div className="option-type-section">
                                        <label>Type:</label>
                                        <select
                                            value={option.type}
                                            onChange={(e) => updateOption(option.id, 'type', e.target.value)}
                                            className="option-type-select"
                                        >
                                            <option value="single">Single Choice</option>
                                            <option value="multiple">Multiple Choice</option>
                                            <option value="checkbox">Checkbox</option>
                                        </select>
                                    </div>
                                )}
                                {option.isCommonOption && !hideCommonOptions && (
                                    <div className="option-type-section">
                                        <label>Type:</label>
                                        <span className="option-type-display">{option.type === 'single' ? 'Single Choice' : option.type === 'multiple' ? 'Multiple Choice' : 'Checkbox'}</span>
                                    </div>
                                )}

                                <div className="option-required-section">
                                    <label>
                                        <input
                                            type="checkbox"
                                            checked={option.required}
                                            onChange={(e) => updateOption(option.id, 'required', e.target.checked)}
                                            disabled={option.type === 'checkbox' || (option.isCommonOption && !hideCommonOptions)}
                                        />
                                        Required
                                    </label>
                                </div>
                            </div>
                        </div>

                        {/* Validation Errors */}
                        {errors[option.id] && (
                            <div className="option-errors">
                                {errors[option.id].map((error, index) => (
                                    <p key={index} className="error-message">{error}</p>
                                ))}
                            </div>
                        )}

                        {/* Choices Section */}
                        <div className="choices-section">
                            <div className="choices-header">
                                <h5>Choices</h5>
                                {!(option.isCommonOption && !hideCommonOptions) && (
                                    <button
                                        type="button"
                                        className="add-choice-btn"
                                        onClick={() => addChoice(option.id)}
                                        disabled={option.type === 'checkbox' && option.choices.length >= 1}
                                    >
                                        + Add Choice
                                    </button>
                                )}
                            </div>

                            <div className="choices-list">
                                {[...option.choices].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)).map((choice, choiceIndex, sortedChoices) => (
                                    <div key={choice.id} className="choice-item-inline">
                                        <div className="choice-inputs">
                                            <div className="reorder-buttons">
                                                <button
                                                    type="button"
                                                    className="reorder-btn"
                                                    onClick={() => moveChoice(option.id, choice.id, 'up')}
                                                    disabled={choiceIndex === 0}
                                                    title="Move choice up"
                                                >
                                                    ↑
                                                </button>
                                                <button
                                                    type="button"
                                                    className="reorder-btn"
                                                    onClick={() => moveChoice(option.id, choice.id, 'down')}
                                                    disabled={choiceIndex === sortedChoices.length - 1}
                                                    title="Move choice down"
                                                >
                                                    ↓
                                                </button>
                                            </div>
                                            <input
                                                type="text"
                                                value={choice.name}
                                                onChange={(e) => updateChoice(option.id, choice.id, 'name', e.target.value)}
                                                className="choice-name-input"
                                                placeholder="Choice name"
                                                disabled={option.isCommonOption && !hideCommonOptions}
                                            />

                                            <div className="choice-price-section">
                                                <div className="choice-cost-section">
                                                    <label>Price: £</label>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={choice.extraCost}
                                                        onChange={(e) => updateChoice(option.id, choice.id, 'extraCost', parseFloat(e.target.value) || 0)}
                                                        className="choice-cost-input"
                                                        disabled={option.isCommonOption && !hideCommonOptions}
                                                    />
                                                </div>
                                            </div>

                                            <label className="choice-default-section">
                                                <input
                                                    type="checkbox"
                                                    checked={choice.defaultSelected || false}
                                                    onChange={(e) => updateChoice(option.id, choice.id, 'defaultSelected', e.target.checked)}
                                                    disabled={option.isCommonOption && !hideCommonOptions}
                                                />
                                                Default
                                            </label>

                                            {!(option.isCommonOption && !hideCommonOptions) && (
                                                <button
                                                    type="button"
                                                    className="delete-choice-btn"
                                                    onClick={() => deleteChoice(option.id, choice.id)}
                                                    disabled={option.choices.length <= 1}
                                                    title="Delete choice"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                        <div className="choice-stock-section">
                                            <ProductStockSelector
                                                stocks={stockListMemo}
                                                selectedStockIds={choice.stockIds ?? []}
                                                onToggleStock={(stockId) => toggleChoiceStock(option.id, choice.id, stockId)}
                                                loading={stocksLoading}
                                                disabled={disabled}
                                                enableQuantityOverrides={false}
                                                modalTitle={`Manage stock for ${option.name} · ${choice.name}`}
                                                modalSubtitle="Select the stock items consumed when this choice is used."
                                                onStocksChanged={async () => {
                                                    if (onRefreshStocks) {
                                                        await onRefreshStocks();
                                                    }
                                                }}
                                            />
                                        </div>
                                        <div className="choice-allergen-section">
                                            <AllergenManager
                                                allergens={choice.allergens || []}
                                                onChange={(allergens) => updateChoice(option.id, choice.id, 'allergens', allergens)}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};
